import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { School, Shield, ArrowRight, Key } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import schoolLogo from '../../assets/school-logo.png';
import heroText from '../../assets/enigma-hero-text.png';

export const LoginPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'school' | 'admin'>('school');
    const [accessKey, setAccessKey] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const { login, loginAdmin, isAdmin, school, isAuthenticated } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (isAuthenticated) {
            if (isAdmin) {
                navigate('/admin/dashboard');
            } else if (school) {
                navigate('/school/dashboard');
            }
        }
    }, [isAuthenticated, isAdmin, school, navigate]);

    const handleSchoolLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!accessKey.trim()) {
            setError('Access key is required');
            return;
        }

        try {
            setError('');
            setLoading(true);
            await login(accessKey);
            navigate('/school/dashboard');
        } catch (err: any) {
            setError(err.message || 'Invalid access key');
        } finally {
            setLoading(false);
        }
    };

    const handleAdminLogin = async () => {
        try {
            setError('');
            setLoading(true);
            await loginAdmin();
        } catch (err: any) {
            setError(err.message || 'Authentication failed');
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0A0A0A] flex">
            {/* ─── Left Panel — Branding ─── */}
            <div className="hidden lg:flex lg:w-[45%] xl:w-[50%] relative overflow-hidden flex-col justify-between p-12">
                {/* Background */}
                <div className="absolute inset-0">
                    <div
                        className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full opacity-[0.1] blur-[120px]"
                        style={{ background: 'radial-gradient(ellipse, #FF991C 0%, #311b92 70%, transparent 100%)' }}
                    />
                    <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.012)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.012)_1px,transparent_1px)] bg-[size:60px_60px]" />
                </div>

                {/* Top — School logo */}
                <div className="relative z-10">
                    <p className="text-white/30 text-[10px] uppercase tracking-[0.35em] mb-4 font-medium">Presented by</p>
                    <img
                        src={schoolLogo}
                        alt="Seth M.R. Jaipuria School"
                        className="w-[200px] h-auto object-contain"
                    />
                </div>

                {/* Center — ENIGMA branding */}
                <div className="relative z-10 flex-1 flex flex-col justify-center -mt-8">
                    <img
                        src={heroText}
                        alt="ENIGMA"
                        className="w-[320px] xl:w-[380px] h-auto object-contain mb-6"
                    />
                    <p className="text-xl xl:text-2xl leading-snug max-w-sm">
                        <span className="text-white/30 font-light">The Inter-School </span>
                        <span className="text-white/70 font-semibold">Tech Symposium</span>
                    </p>
                </div>

                {/* Bottom — Tagline */}
                <div className="relative z-10 flex items-center gap-3">
                    <span className="text-white/25 text-[10px] font-medium tracking-[0.3em] uppercase">Think</span>
                    <span className="w-1 h-1 rounded-full bg-[#FF991C]/40" />
                    <span className="text-white/25 text-[10px] font-medium tracking-[0.3em] uppercase">Solve</span>
                    <span className="w-1 h-1 rounded-full bg-[#311b92]/60" />
                    <span className="text-white/25 text-[10px] font-medium tracking-[0.3em] uppercase">Evolve</span>
                </div>

                {/* Vertical divider */}
                <div className="absolute top-[10%] bottom-[10%] right-0 w-px bg-gradient-to-b from-transparent via-white/[0.06] to-transparent" />
            </div>

            {/* ─── Right Panel — Login Form ─── */}
            <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-8 relative">
                {/* Mobile-only header */}
                <div className="lg:hidden mb-10 flex flex-col items-center text-center">
                    <img
                        src={schoolLogo}
                        alt="Seth M.R. Jaipuria School"
                        className="w-[160px] sm:w-[180px] h-auto object-contain mb-4"
                    />
                    <img
                        src={heroText}
                        alt="ENIGMA"
                        className="w-[200px] sm:w-[240px] h-auto object-contain mb-2"
                    />
                    <p className="text-sm text-white/30">Inter-School Tech Symposium 2026</p>
                </div>

                <div className="w-full max-w-[400px]">
                    {/* Form header */}
                    <div className="mb-8">
                        <h1 className="text-2xl sm:text-3xl font-display font-bold text-white mb-2">
                            Sign in
                        </h1>
                        <p className="text-white/35 text-sm">
                            Access the ENIGMA portal to manage your team.
                        </p>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-1 p-1 bg-white/[0.03] rounded-xl mb-8 border border-white/[0.05]">
                        <button
                            onClick={() => { setActiveTab('school'); setError(''); }}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-medium transition-all duration-300 ${
                                activeTab === 'school'
                                    ? 'bg-[#FF991C] text-black font-semibold shadow-lg'
                                    : 'text-white/35 hover:text-white/60'
                            }`}
                        >
                            <School size={15} />
                            School
                        </button>
                        <button
                            onClick={() => { setActiveTab('admin'); setError(''); }}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-medium transition-all duration-300 ${
                                activeTab === 'admin'
                                    ? 'bg-[#311b92] text-white font-semibold shadow-lg'
                                    : 'text-white/35 hover:text-white/60'
                            }`}
                        >
                            <Shield size={15} />
                            Admin
                        </button>
                    </div>

                    {/* School Login Form */}
                    {activeTab === 'school' && (
                        <form onSubmit={handleSchoolLogin} className="space-y-5 animate-fade-in">
                            <div className="space-y-2">
                                <label className="text-[13px] font-medium text-white/50">Access Key</label>
                                <div className="relative">
                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/25">
                                        <Key size={16} />
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Enter your school access key"
                                        value={accessKey}
                                        onChange={(e) => setAccessKey(e.target.value)}
                                        className="w-full pl-11 pr-4 py-3.5 bg-white/[0.03] border border-white/[0.07] rounded-xl text-white placeholder:text-white/20 focus:border-[#FF991C]/40 focus:ring-2 focus:ring-[#FF991C]/10 focus:outline-none transition-all text-sm"
                                        autoFocus
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="px-4 py-3 bg-[#EF4444]/[0.06] border border-[#EF4444]/15 text-[#EF4444] rounded-xl text-sm">
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#FF991C] text-black font-bold text-sm rounded-xl hover:brightness-110 hover:scale-[1.01] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Signing in...' : 'Continue'}
                                {!loading && <ArrowRight size={16} />}
                            </button>

                            <p className="text-[11px] text-center text-white/20 pt-1">
                                Check your invitation letter for the access key.
                            </p>
                        </form>
                    )}

                    {/* Admin Login Form */}
                    {activeTab === 'admin' && (
                        <div className="space-y-5 animate-fade-in">
                            <div className="p-5 border border-dashed border-white/[0.05] rounded-xl bg-white/[0.015]">
                                <p className="text-[13px] text-white/40 mb-5 text-center leading-relaxed">
                                    Authorized personnel only.<br />
                                    Sign in with your organization Google account.
                                </p>

                                {error && (
                                    <div className="mb-4 px-4 py-3 bg-[#EF4444]/[0.06] border border-[#EF4444]/15 text-[#EF4444] rounded-xl text-sm">
                                        {error}
                                    </div>
                                )}

                                <button
                                    onClick={handleAdminLogin}
                                    disabled={loading}
                                    className="w-full flex items-center justify-center gap-3 py-3.5 bg-white/[0.04] border border-white/[0.08] text-white/80 font-medium text-sm rounded-xl hover:bg-white/[0.07] hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                    </svg>
                                    {loading ? 'Signing in...' : 'Sign in with Google'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Bottom text */}
                <div className="absolute bottom-6 text-center">
                    <p className="text-white/15 text-[10px] tracking-[0.2em] uppercase">
                        ENIGMA 2026 &middot; Seth M.R. Jaipuria School
                    </p>
                </div>
            </div>
        </div>
    );
};
